import { Observable, of, Subject } from 'rxjs';
import { Injectable, NgZone } from '@angular/core';
import { BridgeManagerModule } from './bridge-manager.module';
import {
  BridgeApiEvent,
  CloseRequest,
  DataFromOverlay,
  IsShaking,
  Load,
  SendContact,
  SetMsisdn, SetOtpFlowDoneStatus,
  SetSession
} from './bridge.model';
import { compareVersion, decodeResponseBridge } from '../util.class';
import { LoggerService } from '../common/logger.service';

@Injectable({
  providedIn: BridgeManagerModule,
  deps: [NgZone, LoggerService]
})
export class BridgeApiService {
  readonly events: Observable<BridgeApiEvent> = new Subject<BridgeApiEvent>();

  constructor(private zone: NgZone, private logger: LoggerService) { }

  public static toObject(instance): object {
    return Object.getOwnPropertyNames(this.prototype).reduce(
      (acc, curr) => this.prototype.hasOwnProperty(curr) && { ...acc, [curr]: this.prototype[curr].bind(instance) },
      {}
    );
  }

  isShaking(): void {
    this.zone.run(() => (this.events as Subject<BridgeApiEvent>).next(new IsShaking()));
  }

  reloadFromOverlay(): void {
    console.log('reloadFromOverlay called!');
  }

  // eslint-disable-next-line
  setSession(
    installationId,
    secureKey,
    token,
    loginResponse,
    profileResponse,
    msisdn,
    os,
    tablet,
    notificationsCount, // TODO: remove if not used
    notificationsUnreadCount, // TODO: remove if not used
    version, // TODO: remove if not used
    isOverlay = 'false',
    networkInfo = '',
    deviceUserAgent = '',
    tealiumAccount,
    tealiumUser,
    fixedLineInfo,
    tokenExchangeMap
    //Qui verranno aggiunti due campi
  ): void {
    const userTypeMapping = (profile) => {
      switch (profile) {
      case 'BUSINESS_MASTER_REFERENT':
      case 'BUSINESS_OPERATIVE_REFERENT':
        return 'chooser';
      case 'BUSINESS_EMPLOYEE':
        return 'enduser';
      default:
        return 'anonymous';
      }
    };

    const subscriptionTypeMapping = (profile) => {
      return profile?.billingObjects?.find(d => d?.paymentType?.toUpperCase() === 'POSTPAID') ? 'Paym' : '';
    };

    /*
    if (!loginResponse || !loginResponse.profileData) {
      this.logger.datadog(
        new Error('loginResponse Bridge could be incomplete'), {
          installationId,
          loginResponse
        }
      );
    }
    */

    if (loginResponse && !loginResponse.userInfo) {
      this.logger.datadog(
        new Error('Login Response is complete but profileData is not present.'), {
          installationId,
          loginResponse
        }
      );
    }

    this.zone.run(() =>
      (this.events as Subject<BridgeApiEvent>).next(
        new SetSession(decodeResponseBridge({
          isOverlay: isOverlay === 'true',
          auth: {
            // secureKey, // TODO: remove if not used
            // sessionId: null, // TODO: remove if not used
            token,
            tokenExchangeMap
          },
          customer: (compareVersion(version, '7.7.0') >= 0) ? {
            isDunning: loginResponse.userInfo?.company?.isDunning ?? false,
            isHotline: loginResponse.userInfo?.company?.isHotline ?? false,
            username: loginResponse.userInfo?.username,
            email: loginResponse.userInfo?.email,
            custCode: loginResponse.userInfo?.delegationCustCode,
            userType: userTypeMapping(loginResponse.userInfo?.profileIdentifier),
            walletId: '',
            profile: loginResponse.userInfo?.profileIdentifier,
            name: loginResponse.userInfo?.name,
            surname: loginResponse.userInfo?.surname,
            segment: loginResponse.userInfo?.company?.segment || '',
            isTm9: loginResponse.userInfo?.isTm9 ?? false
          } : {
            isDunning: loginResponse.assetData?.isDunning ?? false,
            isHotline: loginResponse.assetData?.isHotline ?? false,
            username: loginResponse.profileData?.username,
            email: loginResponse.profileData?.email,
            custCode: loginResponse.profileData?.delegationCustCode,
            userType: userTypeMapping(loginResponse.profileData?.profile),
            isEnriched: loginResponse.profileData?.isEnriched,
            walletId: '',
            profile: loginResponse.profileData?.profile,
            name: loginResponse.profileData?.name,
            surname: loginResponse.profileData?.surname,
            segment: loginResponse.assetData?.segment || '',
            isTm9: loginResponse.profileData?.isTm9 ?? false
          },
          currentItem: (compareVersion(version, '7.7.0') >= 0) ? {
            id: profileResponse?.id,
            msisdn,
            isMyMsisdn: !!loginResponse.userInfo?.billingObjects?.find(bo => bo.value === msisdn),//
            code: profileResponse?.tmCode,
            pbo: profileResponse?.hashedValue,
            dual: profileResponse?.isDual,
            type: loginResponse.userInfo?.customerType ? loginResponse.userInfo?.customerType?.toUpperCase() : 'SOHO',//
            customerCodePerson: loginResponse.userInfo?.customerId,
            customerCodeCompany: loginResponse.userInfo?.custcode,
            userPlus: profileResponse?.isUserPlus ?? false,
            subscriptionType: subscriptionTypeMapping(loginResponse.userInfo),
            fixedLineInfo
          } : {
            id: profileResponse.msisdnDetails?.simId,
            msisdn,
            isMyMsisdn: !!loginResponse.billingObjectDataList?.find(d => d.msisdn === msisdn),
            code: profileResponse.msisdnDetails?.ptCode,
            pbo: profileResponse.msisdnDetails?.hashedMsisdn,
            dual: profileResponse.msisdnDetails?.isDual,
            type: loginResponse.profileData?.customerType ? loginResponse.profileData?.customerType?.toUpperCase() : 'SOHO',
            customerCodePerson: loginResponse.profileData?.customerId,
            customerCodeCompany: profileResponse.customerDetails?.custcode40,
            userPlus: profileResponse.msisdnDetails?.isUserPlus ?? false,
            subscriptionType: subscriptionTypeMapping(loginResponse),
            fixedLineInfo
          },
          items: compareVersion(version, '7.7.0') >= 0 ? loginResponse.userInfo.billingObjects || [] : loginResponse.billingObjectDataList || [],
          device: {
            installationId: installationId,
            deviceVendor: 'unknown',
            deviceOs: os,
            deviceModel: 'unknown',
            deviceIsTablet: tablet === 'true',
            appId: 'unknown',
            appPlatform: 'unknown',
            appVersion: version?.split('-')?.length ? version.split('-')[0] : version,
            networkInfo: networkInfo,
            deviceUserAgent
          },
          accountInfo: { tealiumAccountInfo: tealiumAccount ?? '' },
          userInfo: { tealiumUserInfo: tealiumUser ?? '' },
        }) as any),
      ),
    );
  }

  setNetworkStatus(): void { }

  setTealiumTaggingInfo(): void { }

  sendContact(contact): void {
    this.zone.run(() =>
      (this.events as Subject<BridgeApiEvent>).next(
        new SendContact(decodeResponseBridge(contact) as { label: string; msisdn: string }),
      ),
    );
  }

  dataFromOverlay(params): void {
    this.zone.run(() =>
      (this.events as Subject<BridgeApiEvent>).next(new DataFromOverlay(decodeResponseBridge(params)))
    );
  }

  setMsisdn(params): void {
    this.zone.run(() =>
      (this.events as Subject<BridgeApiEvent>).next(new SetMsisdn(params)),
    );
  }

  closeRequest(): void {
    this.zone.run(() =>
      (this.events as Subject<BridgeApiEvent>).next(new CloseRequest()),
    );
  }

  load(url): void {
    this.zone.run(() =>
      (this.events as Subject<BridgeApiEvent>).next(new Load(url))
    );
  }

  setOtpFlowDoneStatus(otpValid) {
    this.zone.run(() =>
      (this.events as Subject<BridgeApiEvent>).next(new SetOtpFlowDoneStatus(otpValid))
    );
  }
}
